/* eslint-disable no-nested-ternary */
import { createSlice } from "@reduxjs/toolkit";
import store, { StoreDispatch } from "../store";
import { SearchUserRequest, searchUser, getUserWallets, getUserCard } from "src/network/graphql/userService";
import { Users } from "../users/usersSlice";
import { SearchWalletRequest, getWalletCards, getWalletUsers, searchWallet } from "src/network/graphql/walletService";
import { Wallet } from "../wallets/walletSlice";
import { Card } from "../cards/cardSlice";
import {
  SinglePayRequest,
  WalletTransferRequest,
  groupPay,
  groupPayRequest,
  internalWalletTransfer,
  singlePay
} from "src/network/graphql/paymentService";
import swal from "sweetalert2";
import {
  TransactionRequest,
  getAllTransaction
} from "src/network/graphql/transactionService";
import { Transaction } from "../transactions/transactionSlice";
import { PRIVATE, SHARED } from "src/components/constant/constant";
import { formatFullName } from "src/utils";
import {
  getGroupCard,
  getGroupDetail,
  getGroupList,
  getGroupUser,
  SearchGroupListRequest
} from "src/network/graphql/groupService";
import { initialGroup } from "../group/groupSlice";
import { initialGroupDetail, initialGroupUser } from "../groupDetail/groupDetailSlice";

const initialUsers: Users[] = [];
const initialWallets: Wallet[] = [];
const initialAllWallets: Wallet[] = [];
const initialCards: Card[] = [];
const initialSearchActivity: Transaction[] = [];

export type TransferSide = "source" | "destination";
const SIDES: TransferSide[] = ["source", "destination"];
const initialWalletSlot = { list: [] as Wallet[], total: 0 };

const LOADABLE_CARD_STATUSES = new Set(["ACTIVE", "HOLD"]);
const isLoadableCard = (card: { card_status?: string } | null | undefined): boolean =>
  !!card?.card_status && LOADABLE_CARD_STATUSES.has(card.card_status);

const newPaymentSlice = createSlice({
  name: "newPayment",
  initialState: {
    userLoading: false,
    allUserLoading: false,
    walletLoading: false,
    walletAllLoading: false,
    userWalletloading: false,
    cardLoading: false,
    singlePayLoading: false,
    groupPayLoading: false,
    internalPayLoading: false,
    searchActivityLoading: false,
    transactionLoading: false,
    allCardloading: false,
    groupsLoading: false,
    groupDetailLoading: false,
    groupCardLoading: false,
    groupUserLoading: false,
    error: null,
    users: {
      list: initialUsers,
      total: 0
    },
    allUsers: {
      list: initialUsers,
      total: 0
    },
    wallets: {
      list: initialWallets,
      total: 0
    },
    cards: {
      list: initialCards,
      total: 0
    },
    allCards: {
      list: initialCards,
      total: 0
    },
    allWallets: {
      list: initialAllWallets,
      total: 0
    },
    userWallet: {
      list: initialAllWallets,
      total: 0
    },
    searchActivity: {
      list: initialSearchActivity,
      total: 0
    },
    groups: {
      list: initialGroup,
      total: 0
    },
    groupCard: {
      list: initialCards,
      total: 0
    },
    groupDetail: initialGroupDetail,
    groupUser: {
      list: initialGroupUser,
      total: 0
    },
    transferWallets: {
      source: { list: initialWallets, total: 0 },
      destination: { list: initialWallets, total: 0 }
    },
    transferWalletLoading: {
      source: false,
      destination: false
    },
    transferGroupDetail: {
      source: initialGroupDetail,
      destination: initialGroupDetail
    },
    transferGroupDetailLoading: {
      source: false,
      destination: false
    }
  },
  reducers: {
    fetchUsersStart: (state) => {
      state.userLoading = true;
    },
    fetchUsersSuccess: (state, action) => {
      state.userLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUsersFail: (state, action) => {
      state.userLoading = false;
      state.users.list = [];
      state.users.total = 0;
      state.error = action.payload;
    },
    fetchAllUsersStart: (state) => {
      state.allUserLoading = true;
    },
    fetchAllUsersSuccess: (state, action) => {
      state.allUserLoading = false;
      state.allUsers.list = action.payload?.list ?? [];
      state.allUsers.total = action.payload?.total ?? 0;
    },
    fetchAllUsersFail: (state, action) => {
      state.allUserLoading = false;
      state.allUsers.list = [];
      state.allUsers.total = 0;
      state.error = action.payload;
    },
    resetUsersAction: (state, action) => {
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchWalletsStart: (state) => {
      state.walletLoading = true;
    },
    fetchWalletsSuccess: (state, action) => {
      state.walletLoading = false;
      state.wallets.list = action.payload?.list ?? [];
      state.wallets.total = action.payload?.total ?? 0;
    },
    fetchWalletsFail: (state, action) => {
      state.walletLoading = false;
      state.wallets.list = [];
      state.wallets.total = 0;
      state.error = action.payload;
    },
    resetwalletListAction: (state, action) => {
      state.wallets.list = action.payload?.list ?? [];
      state.wallets.total = action.payload?.total ?? 0;
    },
    fetchCardsStart: (state) => {
      state.cardLoading = true;
      state.error = null;
    },
    fetchCardsSuccess: (state, action) => {
      state.cardLoading = false;
      state.cards.list = action.payload?.list ?? [];
      state.cards.total = action.payload?.total || 0;
    },
    fetchCardsFail: (state, action) => {
      state.cardLoading = false;
      state.cards.list = [];
      state.cards.total = 0;
      state.error = action.payload;
    },
    resetCardListAction: (state, action) => {
      state.cards.list = action.payload?.list ?? [];
      state.cards.total = action.payload?.total ?? 0;
    },
    singlePayStart: (state) => {
      state.singlePayLoading = true;
    },
    singlePaySuccess: (state) => {
      state.singlePayLoading = false;
      state.error = null;
    },
    singlePayFail: (state, action) => {
      state.singlePayLoading = false;
      state.error = action.payload;
    },
    internalPayStart: (state) => {
      state.internalPayLoading = true;
    },
    internalPaySuccess: (state) => {
      state.internalPayLoading = false;
      state.error = null;
    },
    internalPayFail: (state, action) => {
      state.internalPayLoading = false;
      state.error = action.payload;
    },
    fetchAllWalletsStart: (state) => {
      state.walletAllLoading = true;
    },
    fetchAllWalletsSuccess: (state, action) => {
      state.walletAllLoading = false;
      state.allWallets.list = action.payload?.list ?? [];
      state.allWallets.total = action.payload?.total ?? 0;
    },
    fetchAllWalletsFail: (state, action) => {
      state.walletAllLoading = false;
      state.allWallets.list = [];
      state.allWallets.total = 0;
      state.error = action.payload;
    },
    resetAllWalletAction: (state, action) => {
      state.allWallets.list = action.payload ?? [];
      state.allWallets.total = 0;
    },
    fetchUserWalletStart: (state) => {
      state.userWalletloading = true;
    },
    fetchUserWalletSuccess: (state, action) => {
      state.userWalletloading = false;
      state.userWallet.list = action.payload?.list ?? [];
      state.userWallet.total = action.payload?.total ?? 0;
    },
    fetchUserWalletFail: (state, action) => {
      state.userWalletloading = false;
      state.userWallet.list = [];
      state.userWallet.total = 0;
      state.error = action.payload;
    },
    groupPayStart: (state) => {
      state.groupPayLoading = true;
    },
    groupPaySuccess: (state) => {
      state.groupPayLoading = false;
    },
    groupPayFail: (state, action) => {
      state.groupPayLoading = false;
      state.error = action.payload;
    },
    searchActivityStart: (state) => {
      state.searchActivityLoading = true;
    },
    searchActivitySuccess: (state, action) => {
      state.searchActivityLoading = false;
      state.searchActivity = action.payload;
    },
    searchActivityFail: (state, action) => {
      state.searchActivityLoading = false;
      state.error = action.payload;
    },
    getAllCardsStart: (state) => {
      state.allCardloading = true;
      state.error = null;
    },
    getAllCardsFail: (state, action) => {
      state.allCardloading = false;
      state.allCards.list = [];
      state.allCards.total = 0;
      state.error = action.payload;
    },
    getAllCardsSuccess: (state, action) => {
      state.allCardloading = false;
      state.error = null;
      state.allCards.list = action.payload?.list ?? [];
      state.allCards.total = action.payload?.total ?? 0;
    },
    fetchGroupsStart: (state) => {
      state.groupsLoading = true;
    },
    fetchGroupsSuccess: (state, action) => {
      state.groupsLoading = false;
      state.groups.list = action.payload?.list ?? [];
      state.groups.total = action.payload?.total ?? 0;
    },
    fetchGroupsFail: (state, action) => {
      state.groupsLoading = false;
      state.groups.list = [];
      state.groups.total = 0;
      state.error = action.payload;
    },
    fetchGroupDetailStart: (state) => {
      state.groupDetailLoading = true;
    },
    fetchGroupDetailSuccess: (state, action) => {
      state.groupDetailLoading = false;
      state.groupDetail = action.payload ?? initialGroupDetail;
    },
    fetchGroupDetailFail: (state, action) => {
      state.groupDetailLoading = false;
      state.groupDetail = initialGroupDetail;
      state.error = action.payload;
    },
    fetchGroupCardStart: (state) => {
      state.groupCardLoading = true;
    },
    fetchGroupCardSuccess: (state, action) => {
      state.groupCardLoading = false;
      state.groupCard.list = action.payload?.list ?? [];
      state.groupCard.total = action.payload?.total ?? 0;
    },
    fetchGroupCardFail: (state, action) => {
      state.groupCardLoading = false;
      state.groupCard.list = [];
      state.groupCard.total = 0;
      state.error = action.payload;
    },
    resetGroupDetailAction: (state) => {
      state.groupDetail = initialGroupDetail;
    },
    resetGroupCardAction: (state) => {
      state.groupCard.list = [];
      state.groupCard.total = 0;
    },
    resetGroupUserAction: (state) => {
      state.groupUser.list = [];
      state.groupUser.total = 0;
    },
    fetchGroupUserStart: (state) => {
      state.groupUserLoading = true;
    },
    fetchGroupUserSuccess: (state, action) => {
      state.groupUserLoading = false;
      state.groupUser.list = action.payload?.list ?? [];
      state.groupUser.total = action.payload?.total ?? 0;
    },
    fetchGroupUserFail: (state, action) => {
      state.groupUserLoading = false;
      state.groupUser.list = [];
      state.groupUser.total = 0;
      state.error = action.payload;
    },
    fetchTransferWalletStart: (state, { payload }) => {
      state.transferWalletLoading[payload.side as TransferSide] = true;
    },
    fetchTransferWalletSuccess: (state, { payload }) => {
      const { side, list, total } = payload;

      state.transferWalletLoading[side as TransferSide] = false;
      state.transferWallets[side as TransferSide] = { list: list ?? [], total: total ?? 0 };
    },
    fetchTransferWalletFail: (state, { payload }) => {
      state.transferWalletLoading[payload.side as TransferSide] = false;
      state.transferWallets[payload.side as TransferSide] = { ...initialWalletSlot };
      state.error = payload?.error ?? null;
    },
    resetTransferWalletAction: (state, { payload }) => {
      const sides = payload?.side ? [payload.side as TransferSide] : SIDES;

      sides.forEach((s) => {
        state.transferWallets[s] = { ...initialWalletSlot };
      });
    },
    fetchTransferGroupDetailStart: (state, { payload }) => {
      state.transferGroupDetailLoading[payload.side as TransferSide] = true;
    },
    fetchTransferGroupDetailSuccess: (state, { payload }) => {
      state.transferGroupDetailLoading[payload.side as TransferSide] = false;
      state.transferGroupDetail[payload.side as TransferSide] = payload.data ?? initialGroupDetail;
    },
    fetchTransferGroupDetailFail: (state, { payload }) => {
      state.transferGroupDetailLoading[payload.side as TransferSide] = false;
      state.transferGroupDetail[payload.side as TransferSide] = initialGroupDetail;
      state.error = payload?.error ?? null;
    },
    resetTransferGroupDetailAction: (state, { payload }) => {
      const sides = payload?.side ? [payload.side as TransferSide] : SIDES;

      sides.forEach((s) => {
        state.transferGroupDetail[s] = initialGroupDetail;
      });
    },
    resetAllTransferStateAction: (state) => {
      SIDES.forEach((s) => {
        state.transferWallets[s] = { ...initialWalletSlot };
        state.transferGroupDetail[s] = initialGroupDetail;
        state.transferWalletLoading[s] = false;
        state.transferGroupDetailLoading[s] = false;
      });
    }
  }
});

const {
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  fetchAllUsersStart,
  fetchAllUsersSuccess,
  fetchAllUsersFail,
  fetchWalletsStart,
  fetchWalletsSuccess,
  fetchWalletsFail,
  fetchCardsStart,
  fetchCardsSuccess,
  fetchCardsFail,
  singlePayStart,
  singlePaySuccess,
  singlePayFail,
  fetchAllWalletsStart,
  fetchAllWalletsSuccess,
  fetchAllWalletsFail,
  internalPayStart,
  internalPaySuccess,
  internalPayFail,
  groupPayStart,
  groupPaySuccess,
  groupPayFail,
  fetchUserWalletStart,
  fetchUserWalletSuccess,
  fetchUserWalletFail,
  searchActivityStart,
  searchActivitySuccess,
  searchActivityFail,
  getAllCardsStart,
  getAllCardsSuccess,
  getAllCardsFail,
  fetchGroupsFail,
  fetchGroupsStart,
  fetchGroupsSuccess,
  fetchGroupDetailFail,
  fetchGroupDetailStart,
  fetchGroupDetailSuccess,
  fetchGroupCardFail,
  fetchGroupCardStart,
  fetchGroupCardSuccess,
  fetchGroupUserFail,
  fetchGroupUserStart,
  fetchGroupUserSuccess,
  fetchTransferWalletStart,
  fetchTransferWalletSuccess,
  fetchTransferWalletFail,
  fetchTransferGroupDetailStart,
  fetchTransferGroupDetailSuccess,
  fetchTransferGroupDetailFail
} = newPaymentSlice.actions;

export const {
  resetwalletListAction,
  resetCardListAction,
  resetAllWalletAction,
  resetUsersAction,
  resetGroupCardAction,
  resetGroupDetailAction,
  resetGroupUserAction,
  resetTransferWalletAction,
  resetTransferGroupDetailAction,
  resetAllTransferStateAction
} = newPaymentSlice.actions;

export const fetchUserListAction = (data: SearchUserRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUsersStart());
    try {
      const response = await searchUser({
        user_filter: data.user_filter,
        size: data.size,
        from: data.from
      });

      const structuredResponse = response.data.search_user.user_listing
        // .filter((user: any) => user.current_organisation_role === PARTICIPANT)
        .map((user: any) => ({
          id: user.display_id,
          firstName: user.attributes.first_name,
          lastName: user.attributes.last_name,
          email: user.email,
          currentOrganisationRole: user.current_organisation_role,
          status: user.is_active,
          userId: user.user_id
        }));

      dispatch(
        fetchUsersSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
    } catch (error) {
      dispatch(fetchUsersFail(error));
    }
  };
};

export const fetchWalletListAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletsStart());
    try {
      const reequestData = {
        ...data,
        walletFilter: {
          ...data.walletFilter,
          wallet_status: "ACTIVE"
        }
      };
      const response = await searchWallet(reequestData);

      const structuredResponse = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        // status: wallet.wallet_status,
        walletType: wallet.wallet_type,
        name: wallet.name
        // participant: { role: "PARTICIPANT", fullName: wallet.description }
      }));

      dispatch(
        fetchWalletsSuccess({
          list: structuredResponse,
          total: structuredResponse.length
        })
      );
    } catch (error) {
      dispatch(fetchWalletsFail(error));
    }
  };
};

export const fetchCardListAction = (WalletId: string, userId?: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchCardsStart());
    try {
      const { auth } = store.getState();
      const response = await getWalletCards(WalletId);
      const structuredResponse = response?.data.get_wallet_cards
        ?.filter((card: any) => card?.card_type === SHARED && isLoadableCard(card))
        ?.map((card: any) => ({
          id: card?.card_id,
          cardStatus: card?.card_status,
          cardNumber: card?.ppan,
          user: {
            id: card?.user_id,
            fullName: card?.holder_name
              ? card?.holder_name
              : card?.user
                ? formatFullName(card?.user?.attributes.first_name, card?.user?.attributes.last_name)
                : "---"
          }
        }));

      const currentUserResponse = await getUserCard(
        String(auth?.userInfo?.id),
        String(auth?.cognitoConfig?.currentOrganisation?.organisation_id)
      );

      let otherUserResponse = null;

      if (userId && userId !== auth?.userInfo?.id) {
        otherUserResponse = await getUserCard(
          String(userId),
          String(auth?.cognitoConfig?.currentOrganisation?.organisation_id)
        );
      }

      const currentUserCards =
        currentUserResponse?.data?.get_user_cards?.cards
          ?.filter((card: any) => card?.card_type === PRIVATE && isLoadableCard(card))
          ?.map((card: any) => ({
            id: card?.card_id,
            cardStatus: card?.card_status,
            cardNumber: card?.ppan,
            user: {
              id: card?.user_id,
              fullName: card?.holder_name
                ? card?.holder_name
                : card?.user
                  ? formatFullName(card?.user?.attributes.first_name, card?.user?.attributes.last_name)
                  : "---"
            }
          })) || [];

      const otherUserCards =
        otherUserResponse?.data?.get_user_cards?.cards
          ?.filter((card: any) => card?.card_type === PRIVATE && isLoadableCard(card))
          ?.map((card: any) => ({
            id: card?.card_id,
            cardStatus: card?.card_status,
            cardNumber: card?.ppan,
            user: {
              id: card?.user_id,
              fullName: card?.holder_name
                ? card?.holder_name
                : card?.user
                  ? formatFullName(card?.user?.attributes.first_name, card?.user?.attributes.last_name)
                  : "---"
            }
          })) || [];

      const allCards = [...structuredResponse, ...currentUserCards, ...otherUserCards];
      const uniqueCardList = Array.from(new Map(allCards.map((card) => [card.id, card])).values());

      dispatch(
        fetchCardsSuccess({
          list: uniqueCardList,
          total: uniqueCardList.length
        })
      );
    } catch (error) {
      dispatch(fetchCardsFail(error));
    }
  };
};

export const getAllCardsAction = (walletId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getAllCardsStart());
    try {
      const response = await getWalletCards(walletId);
      const structuredResponse = response?.data.get_wallet_cards
        // ?.filter((card: any) => card?.card_status === "ACTIVE")
        ?.map((card: any) => ({
          id: card?.card_id,
          cardStatus: card?.card_status,
          cardNumber: card?.ppan,
          user: {
            id: card?.user_id,
            fullName: card?.holder_name
              ? card?.holder_name
              : card?.user
                ? formatFullName(card?.user?.attributes.first_name, card?.user?.attributes.last_name)
                : "---"
          }
        }));

      dispatch(
        getAllCardsSuccess({
          list: structuredResponse,
          total: structuredResponse.length
        })
      );
    } catch (error) {
      dispatch(getAllCardsFail(error));
    }
  };
};

export const singlePayAction = (data: SinglePayRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(singlePayStart());
    try {
      const response = await singlePay(data);

      dispatch(singlePaySuccess());
      swal.fire({
        position: "center",
        icon: "success",
        title: "Success! You're ready to pay",
        showConfirmButton: false,
        timer: 2500
      });
      if (cb) cb();
      return response;
    } catch (err) {
      dispatch(singlePayFail(err));
    }
  };
};

export const internalPayAction = (data: WalletTransferRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(internalPayStart());
    try {
      await internalWalletTransfer(data);

      swal.fire({
        position: "center",
        icon: "success",
        title: "Congratulations!  Transfer was completed successfully.",
        showConfirmButton: false,
        timer: 2500
      });
      if (cb) cb();
      dispatch(internalPaySuccess());
    } catch (err) {
      dispatch(internalPayFail(err));
    }
  };
};

export const fetchAllWalletListAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchAllWalletsStart());
    try {
      const reequestData = {
        ...data,
        walletFilter: {
          ...data.walletFilter,
          wallet_status: "ACTIVE"
        }
      };
      const response = await searchWallet(reequestData);

      const structuredResponse = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        status: wallet.wallet_status,
        name: wallet.name,
        participant: { role: "PARTICIPANT", fullName: wallet.description, email: wallet?.user?.email }
      }));

      dispatch(
        fetchAllWalletsSuccess({
          list: structuredResponse,
          total: response.data.search_wallet.total_size
        })
      );
    } catch (error) {
      dispatch(fetchAllWalletsFail(error));
    }
  };
};

export const fetchAllUser = (walletId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchAllUsersStart());
    try {
      const response = await getWalletUsers(walletId);

      const structuredResponse = response.data.get_wallet_users.map((user: any) => ({
        id: user.display_id,
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
        currentOrganisationRole: user.current_organisation_role,
        email: user.email,
        userId: user.user_id
      }));

      dispatch(
        fetchAllUsersSuccess({
          list: structuredResponse,
          total: structuredResponse.length
        })
      );
    } catch (error) {
      dispatch(fetchAllUsersFail(error));
    }
  };
};

export const groupPayAction = (data: groupPayRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(groupPayStart());
    try {
      const response = await groupPay(data);

      dispatch(groupPaySuccess());
      return response;
    } catch (err) {
      dispatch(groupPayFail(err));
    }
  };
};

export const fetchUserWalletAction = (userId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUserWalletStart());
    try {
      const response = await getUserWallets(userId);
      const spendingWallet = response.data.get_user_wallets.find((wallet: any) => wallet.wallet_type === "SPENDING");

      dispatch(
        fetchUserWalletSuccess({
          list: [
            {
              id: spendingWallet.wallet_id,
              displayId: spendingWallet.display_id,
              description: spendingWallet.description,
              balance: spendingWallet.balance,
              // status: wallet.wallet_status,
              walletType: spendingWallet.wallet_type,
              name: spendingWallet.name
            }
          ],
          total: 1
        })
      );
    } catch (error) {
      dispatch(fetchUserWalletFail(error));
    }
  };
};

export const searchActivityAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(searchActivityStart());
    try {
      const response = await getAllTransaction(data);
      const structuredResponse = response.data.search_activity.activity_listing.map((activity: any) => ({
        id: activity.activity_id,
        data: JSON.parse(activity.data)
      }));

      dispatch(
        searchActivitySuccess({
          list: structuredResponse,
          total: response.data.search_activity.total_size
        })
      );
    } catch (err) {
      dispatch(searchActivityFail(err));
    }
  };
};

export const fetchGroupListAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupsStart());
    try {
      const response = await getGroupList(data);

      const structuredResponse = response.data.search_group.items;

      dispatch(
        fetchGroupsSuccess({
          list: structuredResponse,
          total: response.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchGroupsFail(error));
    }
  };
};

export const fetchGroupDetailAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupDetailStart());
    try {
      const response = await getGroupDetail(groupId);

      dispatch(fetchGroupDetailSuccess(response.data.get_group));
    } catch (error) {
      dispatch(fetchGroupDetailFail(error));
    }
  };
};

export const getGroupCardAction = (id: string, walletId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupCardStart());
    try {
      const [groupCardResponse, walletCardResponse] = await Promise.all([getGroupCard(id), getWalletCards(walletId)]);

      const groupCards =
        groupCardResponse?.data.get_group_cards
          ?.filter((card: any) => isLoadableCard(card))
          ?.map((card: any) => ({
            id: card?.card_id,
            cardStatus: card?.card_status,
            cardNumber: card?.ppan,
            user: {
              id: card?.user_id,
              fullName: card?.holder_name
                ? card?.holder_name
                : card?.user
                  ? formatFullName(card?.user?.attributes.first_name, card?.user?.attributes.last_name)
                  : "---"
            }
          })) || [];

      const walletCards =
        walletCardResponse?.data.get_wallet_cards
          ?.filter((card: any) => card?.card_type === SHARED && isLoadableCard(card))
          ?.map((card: any) => ({
            id: card?.card_id,
            cardStatus: card?.card_status,
            cardNumber: card?.ppan,
            user: {
              id: card?.user_id,
              fullName: card?.holder_name
                ? card?.holder_name
                : card?.user
                  ? formatFullName(card?.user?.attributes.first_name, card?.user?.attributes.last_name)
                  : "---"
            }
          })) || [];

      const allCards = [...groupCards, ...walletCards];
      const uniqueCards = allCards.filter((card, index, self) => index === self.findIndex((c) => c.id === card.id));

      dispatch(
        fetchGroupCardSuccess({
          list: uniqueCards,
          total: uniqueCards.length
        })
      );
    } catch (err) {
      dispatch(fetchGroupCardFail(err));
    }
  };
};

export const getGroupUserAction = (id: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupUserStart());
    try {
      const response = await getGroupUser(id);

      dispatch(
        fetchGroupUserSuccess({
          list: response.data.get_group_users,
          total: response.data.get_group_users.length
        })
      );
    } catch (err) {
      dispatch(fetchGroupUserFail(err));
    }
  };
};

export const fetchTransferWalletListAction = (data: SearchWalletRequest, side: TransferSide) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransferWalletStart({ side }));
    try {
      const reequestData = {
        ...data,
        walletFilter: {
          ...data.walletFilter,
          wallet_status: "ACTIVE"
        }
      };
      const response = await searchWallet(reequestData);

      const structuredResponse = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        walletType: wallet.wallet_type,
        name: wallet.name
      }));

      dispatch(
        fetchTransferWalletSuccess({
          side,
          list: structuredResponse,
          total: structuredResponse.length
        })
      );
    } catch (error) {
      dispatch(fetchTransferWalletFail({ side, error }));
    }
  };
};

export const fetchTransferGroupDetailAction = (groupId: string, side: TransferSide) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransferGroupDetailStart({ side }));
    try {
      const response = await getGroupDetail(groupId);

      dispatch(fetchTransferGroupDetailSuccess({ side, data: response.data.get_group }));
    } catch (error) {
      dispatch(fetchTransferGroupDetailFail({ side, error }));
    }
  };
};

export default newPaymentSlice.reducer;
